import { useCallback, useState } from "react";
import { toast } from "sonner";
import { apiClient, API_PREFIX } from "@/lib/api";

const API = API_PREFIX;

// Wizard state machine — kept flat (no library) since this is a 4-step
// admin-only workflow. Backend logs the audit trail; UI just routes the
// user through the gates.
const STEPS = {
  IDLE: "idle",
  STOPPING: "stopping",
  STOPPED: "stopped",
  CLEARING: "clearing",
  CLEARED: "cleared",
  STARTING: "starting",
  DONE: "done",
};

const TYPED_CONFIRM_TOKEN = "RECOVER";

function SnapshotTable({ snapshot }) {
  if (!snapshot) {
    return <div className="text-zinc-500 text-sm">No snapshot loaded.</div>;
  }
  const liveRows = Object.entries(snapshot.live_stream_depths || {});
  return (
    <div className="text-xs space-y-2">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <span className="text-zinc-400">Cron enabled</span>
        <span className="font-mono text-zinc-100">
          {snapshot.cron_enabled ? "true" : "false"}
        </span>
        <span className="text-zinc-400">Running parents</span>
        <span className="font-mono text-zinc-100">{snapshot.running_parents}</span>
        <span className="text-zinc-400">Queued parents</span>
        <span className="font-mono text-zinc-100">{snapshot.queued_parents}</span>
        {snapshot.oldest_running_iso && (
          <>
            <span className="text-zinc-400">Oldest running</span>
            <span className="font-mono text-zinc-300">
              {snapshot.oldest_running_iso}
            </span>
          </>
        )}
        <span className="text-zinc-400">Parent state keys</span>
        <span className="font-mono text-zinc-100">
          {snapshot.parent_state_keys}
        </span>
        <span className="text-zinc-400">Latest pointer keys</span>
        <span className="font-mono text-zinc-100">
          {snapshot.latest_pointer_keys}
        </span>
        <span className="text-zinc-400">Archive queue length</span>
        <span className="font-mono text-zinc-100">
          {snapshot.archive_queue_length}
        </span>
      </div>
      {liveRows.length > 0 && (
        <div className="pt-2 border-t border-zinc-800">
          <div className="text-zinc-400 mb-1">Live stream depths</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {liveRows.map(([currency, depth]) => (
              <div
                key={currency}
                className="flex justify-between font-mono text-zinc-200"
              >
                <span>{currency}</span>
                <span>{depth}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CleanupSummary({ result }) {
  if (!result) return null;
  const failed = result.failures && result.failures.length > 0;
  return (
    <div className="text-xs space-y-1">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <span className="text-zinc-400">Actor</span>
        <span className="font-mono text-zinc-100">{result.actor_email}</span>
        <span className="text-zinc-400">Parents cancelled</span>
        <span className="font-mono text-zinc-100">{result.parents_cancelled}</span>
        <span className="text-zinc-400">Streams trimmed</span>
        <span className="font-mono text-zinc-100">{result.streams_trimmed}</span>
        <span className="text-zinc-400">Stream entries removed</span>
        <span className="font-mono text-zinc-100">
          {result.stream_entries_removed}
        </span>
        <span className="text-zinc-400">Parent state keys deleted</span>
        <span className="font-mono text-zinc-100">
          {result.parent_state_keys_deleted}
        </span>
        <span className="text-zinc-400">Latest pointer keys deleted</span>
        <span className="font-mono text-zinc-100">
          {result.latest_pointer_keys_deleted}
        </span>
        <span className="text-zinc-400">Archive queue cleared</span>
        <span className="font-mono text-zinc-100">
          {result.archive_queue_cleared
            ? `yes (${result.archive_entries_removed})`
            : "no"}
        </span>
      </div>
      {failed && (
        <div className="pt-2 border-t border-zinc-800">
          <div className="text-red-400 mb-1">Failures</div>
          <ul className="font-mono text-zinc-300 list-disc pl-5">
            {result.failures.map((failure, idx) => (
              <li key={idx}>{failure}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function RecoveryPanel() {
  const [step, setStep] = useState(STEPS.IDLE);
  const [snapshot, setSnapshot] = useState(null);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [includeArchive, setIncludeArchive] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState(null);
  const [adminBlocked, setAdminBlocked] = useState(false);

  const isAdmin = !adminBlocked;

  const reset = useCallback(() => {
    setStep(STEPS.IDLE);
    setSnapshot(null);
    setCleanupResult(null);
    setIncludeArchive(false);
    setConfirmText("");
    setError(null);
  }, []);

  const handleStop = async () => {
    setStep(STEPS.STOPPING);
    setError(null);
    try {
      const res = await apiClient.post(`${API}/jobs/recovery/stop`);
      setSnapshot(res.data?.snapshot ?? null);
      setStep(STEPS.STOPPED);
      toast.success("Scheduler stopped");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setAdminBlocked(true);
        setStep(STEPS.IDLE);
        toast.error("Admin role required for recovery");
        return;
      }
      setError(err?.response?.data?.detail || err.message);
      setStep(STEPS.IDLE);
      toast.error("Failed to stop scheduler");
    }
  };

  const handleRefreshSnapshot = async () => {
    setError(null);
    try {
      const res = await apiClient.get(`${API}/jobs/recovery/snapshot`);
      setSnapshot(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      toast.error("Failed to refresh snapshot");
    }
  };

  const handleClear = async () => {
    if (confirmText !== TYPED_CONFIRM_TOKEN) {
      toast.error(`Type ${TYPED_CONFIRM_TOKEN} to confirm`);
      return;
    }
    setStep(STEPS.CLEARING);
    setError(null);
    try {
      const res = await apiClient.post(
        `${API}/jobs/recovery/cleanup`,
        { include_archive_queue: includeArchive },
        { headers: { "X-Confirm-Recovery": TYPED_CONFIRM_TOKEN } }
      );
      setCleanupResult(res.data);
      setStep(STEPS.CLEARED);
      toast.success(
        `Cleared ${res.data.parents_cancelled} parents, ${res.data.stream_entries_removed} stream entries`
      );
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setStep(STEPS.STOPPED);
      toast.error("Cleanup failed");
    }
  };

  const handleStart = async () => {
    setStep(STEPS.STARTING);
    setError(null);
    try {
      const res = await apiClient.post(`${API}/jobs/recovery/start`);
      setSnapshot(res.data?.snapshot ?? null);
      setStep(STEPS.DONE);
      toast.success("Scheduler restarted");
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setStep(STEPS.CLEARED);
      toast.error("Failed to restart scheduler");
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-md p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-lg text-zinc-100 font-semibold">Queue Recovery</h3>
          <p className="text-zinc-500 text-xs mt-0.5">
            Admin-only. Stop scheduler → review stuck state → clear → restart.
          </p>
        </div>
        {step !== STEPS.IDLE && step !== STEPS.DONE && (
          <button
            onClick={reset}
            className="text-xs text-zinc-400 hover:text-zinc-200"
          >
            Cancel
          </button>
        )}
      </div>

      {error && (
        <div className="mb-3 text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-2 py-1.5">
          {error}
        </div>
      )}

      {/* Step 1: idle → stop */}
      {step === STEPS.IDLE && (
        <div className="space-y-2">
          <p className="text-zinc-400 text-sm">
            Use this when parent tasks are stuck in "running" or live queues
            won't drain. Cleanup is logged with your email for audit.
          </p>
          <button
            onClick={handleStop}
            className="w-full bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium rounded-md px-3 py-2"
          >
            1. Stop scheduler & inspect
          </button>
        </div>
      )}

      {step === STEPS.STOPPING && (
        <div className="text-zinc-400 text-sm">Stopping scheduler...</div>
      )}

      {/* Step 2: stopped → review */}
      {step === STEPS.STOPPED && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm text-zinc-200 font-medium">Stuck state</h4>
            <button
              onClick={handleRefreshSnapshot}
              className="text-xs text-zinc-400 hover:text-zinc-200"
            >
              Refresh
            </button>
          </div>
          <SnapshotTable snapshot={snapshot} />
          <div className="pt-2 border-t border-zinc-800 space-y-2">
            <label className="flex items-center gap-2 text-xs text-zinc-300">
              <input
                type="checkbox"
                checked={includeArchive}
                onChange={(event) => setIncludeArchive(event.target.checked)}
              />
              Also clear archive queue (
              {snapshot?.archive_queue_length ?? 0} entries)
            </label>
            <label className="block text-xs text-zinc-300">
              Type <span className="font-mono text-amber-400">RECOVER</span> to
              confirm
              <input
                type="text"
                value={confirmText}
                onChange={(event) => setConfirmText(event.target.value)}
                className="block w-full mt-1 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-100 font-mono text-xs"
                placeholder="RECOVER"
              />
            </label>
            <button
              onClick={handleClear}
              disabled={confirmText !== TYPED_CONFIRM_TOKEN}
              className="w-full bg-red-600 hover:bg-red-500 text-white text-sm font-medium rounded-md px-3 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              2. Clear stuck state
            </button>
          </div>
        </div>
      )}

      {step === STEPS.CLEARING && (
        <div className="text-zinc-400 text-sm">Executing cleanup...</div>
      )}

      {/* Step 3: cleared → restart */}
      {step === STEPS.CLEARED && (
        <div className="space-y-3">
          <h4 className="text-sm text-zinc-200 font-medium">Cleanup result</h4>
          <CleanupSummary result={cleanupResult} />
          <button
            onClick={handleStart}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-md px-3 py-2"
          >
            3. Restart scheduler
          </button>
        </div>
      )}

      {step === STEPS.STARTING && (
        <div className="text-zinc-400 text-sm">Starting scheduler...</div>
      )}

      {step === STEPS.DONE && (
        <div className="space-y-3">
          <div className="text-emerald-400 text-sm font-medium">
            Recovery complete. Scheduler is running.
          </div>
          <SnapshotTable snapshot={snapshot} />
          <button
            onClick={reset}
            className="w-full bg-zinc-700 hover:bg-zinc-600 text-zinc-100 text-sm font-medium rounded-md px-3 py-2"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
